/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Build 91 P0-14 — minimal SOS state machine that runs regardless of
// whether the user has set up SafeCircle profiles. The existing
// useSOSEvent (under hooks/) implements a richer flow gated on a
// ProtectedProfile (audio recording, fan-out to circle members,
// stealth mode, etc.) — that path remains Phase 2 once SMS fan-out
// lands. For now we ship a baseline: tap SOS → 5s countdown
// (cancellable) → write a shallow /sosEvents/{uid}/{eventId} record
// with location + timestamp → an Active screen with End SOS.
//
// Safety-critical surface — keep the state machine small and the
// timer cleanup bulletproof. Any state change other than via the
// exposed arm / cancel / endActive helpers is a bug.
//
// All RTDB writes route through `writeSosEvent` (PR #54 helper) so
// the SimpleSOS lane shares the canonical
// `sos.rtdb_write_started/_succeeded/_failed` breadcrumb trail and
// `area:"sos"` Sentry tag with the SafeCircle elaborate lane.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  getDatabase,
  push,
  ref,
  serverTimestamp,
  set,
  update,
} from "firebase/database";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { logUserAction } from "@/utils/errorLogger";
import { writeSosEvent } from "@/utils/safecircle/sosEventWrite";

export type SimpleSOSStatus = "idle" | "countdown" | "active";

// 5 seconds per spec (matches CLAUDE.md). Constant exported for tests /
// UI progress-ring sizing; do NOT inline it in the timer below.
export const SIMPLE_SOS_COUNTDOWN_MS = 5000;
const TICK_MS = 100;

export interface SimpleSOSEvent {
  eventId: string;
  triggeredAt: number;
  lat: number | null;
  lng: number | null;
}

export interface SimpleSOSContextValue {
  status: SimpleSOSStatus;
  /** Milliseconds left in the current countdown. Always 0 when not in countdown. */
  countdownRemainingMs: number;
  /** Populated when status === "active". null otherwise. */
  activeEvent: SimpleSOSEvent | null;
  /** Begin a countdown. No-op if already countdown / active. */
  arm: (snapshot: { lat: number | null; lng: number | null }) => void;
  /** Abort the countdown. No-op if not in countdown. */
  cancel: () => void;
  /** End an active SOS — writes a `resolvedAt` + `status:"resolved"` field
   *  to the RTDB record so watchers see the session has ended. */
  endActive: () => Promise<void>;
}

const SimpleSOSContext = createContext<SimpleSOSContextValue | null>(null);

export function useSimpleSOS(): SimpleSOSContextValue {
  const ctx = useContext(SimpleSOSContext);
  if (!ctx) throw new Error("useSimpleSOS must be used within SimpleSOSProvider");
  return ctx;
}

export function SimpleSOSProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState<SimpleSOSStatus>("idle");
  const [countdownRemainingMs, setCountdownRemainingMs] = useState(0);
  const [activeEvent, setActiveEvent] = useState<SimpleSOSEvent | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const armSnapshotRef = useRef<{ lat: number | null; lng: number | null } | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cleanup on unmount — React 18 StrictMode double-mounts the
  // provider in dev, so this MUST clear any interval the first mount
  // started. The remount creates a fresh ref / state and starts a
  // fresh timer only when the user arms it again.
  useEffect(() => clearTimer, [clearTimer]);

  const writeEvent = useCallback(
    async (snapshot: { lat: number | null; lng: number | null }): Promise<SimpleSOSEvent | null> => {
      if (!user?.uid) return null;
      const database = getDatabase();
      // Shallow path per spec — /sosEvents/{uid}/{eventId}. Distinct
      // from the SafeCircle elaborate path which lives at
      // /safecircle/{uid}/sosEvents/{profileId}/{eventId}.
      const collectionRef = ref(database, `sosEvents/${user.uid}`);
      const eventRef = push(collectionRef);
      const eventId = eventRef.key;
      if (!eventId) return null;
      const triggeredAt = Date.now();
      const record = {
        eventId,
        triggeredAt: serverTimestamp(),
        lat: snapshot.lat,
        lng: snapshot.lng,
        status: "active",
        resolvedAt: null,
      };
      // Route through PR #54's writeSosEvent so the SimpleSOS lane
      // shares the canonical breadcrumb trail and area:"sos" tag on
      // failure. writeSosEvent never throws — on RTDB failure the
      // user still sees the active overlay (with tel:911) because we
      // flip status to "active" below regardless.
      await writeSosEvent(
        {
          setEvent: () => set(eventRef, record),
          addBreadcrumb: (b) => Sentry.addBreadcrumb(b),
          captureException: (err, ctx) => Sentry.captureException(err, ctx),
        },
        { uid: user.uid, eventId, lane: "simple", phase: "fire" },
      );
      return { eventId, triggeredAt, lat: snapshot.lat, lng: snapshot.lng };
    },
    [user],
  );

  const fire = useCallback(async () => {
    const snapshot = armSnapshotRef.current ?? { lat: null, lng: null };
    armSnapshotRef.current = null;
    setCountdownRemainingMs(0);
    Sentry.addBreadcrumb({
      category: "sos",
      level: "info",
      message: "sos.simple.fired",
      data: {
        hasLocation: typeof snapshot.lat === "number" && typeof snapshot.lng === "number",
      },
    });
    const evt = await writeEvent(snapshot);
    if (evt) {
      setActiveEvent(evt);
      setStatus("active");
      logUserAction("simple-sos-fired", {
        eventId: evt.eventId,
        hasLocation: typeof evt.lat === "number" && typeof evt.lng === "number",
      });
    } else {
      // No user → roll back to idle silently. The button shouldn't
      // have rendered in this state but defense-in-depth is cheap.
      setStatus("idle");
    }
  }, [writeEvent]);

  const arm = useCallback(
    (snapshot: { lat: number | null; lng: number | null }) => {
      if (status !== "idle") return;
      armSnapshotRef.current = snapshot;
      setStatus("countdown");
      setCountdownRemainingMs(SIMPLE_SOS_COUNTDOWN_MS);
      const startedAt = Date.now();
      Sentry.addBreadcrumb({
        category: "sos",
        level: "info",
        message: "sos.simple.armed",
        data: {
          hasLocation: typeof snapshot.lat === "number" && typeof snapshot.lng === "number",
          countdownMs: SIMPLE_SOS_COUNTDOWN_MS,
        },
      });
      logUserAction("simple-sos-armed", {
        hasLocation: typeof snapshot.lat === "number" && typeof snapshot.lng === "number",
      });
      clearTimer();
      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, SIMPLE_SOS_COUNTDOWN_MS - elapsed);
        setCountdownRemainingMs(remaining);
        if (remaining <= 0) {
          clearTimer();
          void fire();
        }
      }, TICK_MS);
    },
    [status, fire, clearTimer],
  );

  const cancel = useCallback(() => {
    if (status !== "countdown") return;
    clearTimer();
    armSnapshotRef.current = null;
    setCountdownRemainingMs(0);
    setStatus("idle");
    Sentry.addBreadcrumb({
      category: "sos",
      level: "info",
      message: "sos.simple.cancelled",
    });
    logUserAction("simple-sos-cancelled");
    toast.info(t("sos.simple.cancelledToast"));
  }, [status, clearTimer, t]);

  const endActive = useCallback(async () => {
    if (status !== "active" || !activeEvent) return;
    const eventId = activeEvent.eventId;
    setStatus("idle");
    setActiveEvent(null);
    Sentry.addBreadcrumb({
      category: "sos",
      level: "info",
      message: "sos.simple.ended",
      data: { eventId },
    });
    logUserAction("simple-sos-ended", { eventId });
    if (!user?.uid) return;
    const database = getDatabase();
    await writeSosEvent(
      {
        setEvent: () =>
          update(ref(database, `sosEvents/${user.uid}/${eventId}`), {
            status: "resolved",
            resolvedAt: serverTimestamp(),
          }),
        addBreadcrumb: (b) => Sentry.addBreadcrumb(b),
        captureException: (err, ctx) => Sentry.captureException(err, ctx),
      },
      { uid: user.uid, eventId, lane: "simple", phase: "resolve" },
    );
  }, [status, activeEvent, user]);

  const value = useMemo<SimpleSOSContextValue>(
    () => ({ status, countdownRemainingMs, activeEvent, arm, cancel, endActive }),
    [status, countdownRemainingMs, activeEvent, arm, cancel, endActive],
  );

  return <SimpleSOSContext.Provider value={value}>{children}</SimpleSOSContext.Provider>;
}
